import { IsStringEmpty, isStringNotEmpty, isVarNotEmpty } from './GlobalFunctions'
import { EMPTY_STRING } from '../constants/Common'
import { COMMA } from '../constants/Separators'

/**
 * A set of String utilities.
 */
export class StringUtils {
    public static INTERPOLATION_PLACEHOLDER: string = '{}'

    /**
     * Interpolate a given string.
     * Strings to format should be given with replacement placeholders in the format {}
     *
     * @param str - Initial string to format.
     * @param interpolations - Strings to replace in order.
     *
     * @returns - Interpolated string.
     */
    public static interpolate(str: string, ...interpolations: (Nullable<string> | Nullable<number>)[]): string {
        return interpolations.reduce(
            (accum: string, next: Nullable<string> | Nullable<number>): string =>
                accum?.replace(StringUtils.INTERPOLATION_PLACEHOLDER, `${next}`) ?? EMPTY_STRING,
            str
        )
    }

    /**
     * Split a string at a given index.
     *
     * @param str - String to split.
     * @param index - Index to split at.
     * @returns - Array of two strings.
     */
    public static splitAt(str: string, index: number): [string, string] {
        if (!IsStringEmpty(str) && isVarNotEmpty(index) && str.length > index) {
            return [str.substring(0, index), str.substring(index)]
        }

        return [str.substring(0, str.length), '']
    }

    /**
     * Find the range of the closest surrounding strings to a given index.
     *
     * @param str - String to search.
     * @param index - Index to search around.
     * @param surrounders - Surrounding strings to search for.
     *
     * @returns - Range of the closest surrounding string.
     */
    public static findEntryRange(str: string, index: number, ...surrounders: string[]): [number, number] {
        if (!IsStringEmpty(str) && isVarNotEmpty(index) && surrounders?.length > 0) {
            const rangeSplit: string[] = StringUtils.splitAt(str, index)
            const beforeArray: string[] = rangeSplit[0]?.split('')
            const lastSurrounders: number[] = surrounders
                .map((surrounder: string): number => beforeArray.lastIndexOf(surrounder))
                .filter((surrounderIndex: number): boolean => surrounderIndex > -1)
            const selectStart: number = Math.max(...lastSurrounders) + 1

            const afterArray: string[] = rangeSplit[1]?.split('')
            const firstSurrounders: number[] = surrounders
                .map((surrounder: string): number => afterArray.indexOf(surrounder))
                .filter((surrounderIndex: number): boolean => surrounderIndex > -1)
            const selectEnd: number =
                beforeArray.length + (firstSurrounders.length > 0 ? Math.min(...firstSurrounders) : afterArray.length)

            return [selectStart, selectEnd]
        }

        return [index, index]
    }

    public static inverseSlice = (str: string, start: number, end?: number): string =>
        str.slice(0, start) + (end ? str.slice(end) : '')

    public static replaceRange = (str: string, replacement: string, start: number, end?: number): string =>
        str.substring(0, start) + replacement + (end ? str.substring(end) : EMPTY_STRING)

    public static convertToNumberIfPossible = (value: Nullable<string>): Nullable<number | string> => {
        if (IsStringEmpty(value)) {
            return value
        }

        const normalisedValue: string = value.replace(/^(\d*)x?(\d*)$/, '$1$2')
        const parsedValue: number = Number(isStringNotEmpty(normalisedValue) ? normalisedValue : value)
        return isNaN(parsedValue) ? value : parsedValue
    }

    public static convertCamelCaseToKebabCase = (str: Nullable<string>): Nullable<string> =>
        str?.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1-$2')?.toLowerCase()

    // Replace string array items in string
    public static replaceArrayItemsInString = (str: string, items: string[], replacement: string): string =>
        items.reduce((accum: string, item: string): string => accum.replace(item, replacement), str)

    /**
     * Move a specific string (e.g., "S=1;") to the front of a comma-separated string.
     * - If the field is already first (even with leading/trailing spaces), returns input unchanged.
     * - If the field doesn't exist, returns input unchanged.
     *
     * @param {string} inputString - The source string (comma-separated)
     * @param {string} targetField - The exact field to bring to the front
     * @returns {string} The transformed string
     */
    static bringSubStringToStart(inputString: string, targetField?: string): string {
        if (IsStringEmpty(targetField)) return inputString

        const parts: string[] = inputString.split(COMMA)

        const idx: number = parts.findIndex((p: string): boolean => p.trim() === targetField)

        if (idx <= 0) return inputString

        const [matchedToken] = parts.splice(idx, 1)
        parts.unshift(matchedToken)

        return parts.join(COMMA)
    }

    /**
     * Capitalize the first letter of a string.
     *
     * @param str - The string to capitalize.
     */
    public static capitalizeFirstLetter = (str: Nullable<string>): string => {
        if (IsStringEmpty(str)) {
            return EMPTY_STRING
        }

        return str.charAt(0).toUpperCase() + str.slice(1)
    }
}
